import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles, Button, Typography } from '@material-ui/core';
import axios from 'axios';
import { red } from '@material-ui/core/colors';
import { Auth } from 'aws-amplify';
import TextField from '@material-ui/core/TextField';
import { withRouter } from 'react-router-dom';
import globalStyles from '../styles';
import PhoneNumberInput from './PhoneNumberInput';
import {
    getInvalidPhoneNumberMessage,
    isValidPhoneNumber
} from '../helpers/errors';

const styles = theme => ({
    buttons: {
        marginTop: '20px',
        display: 'block'
    },
    button: {
        margin: '5px 5px'
    },
    icon: {
        fontSize: '16px',
        marginLeft: '5px'
    },
    mobileButtons: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            justifyContent: 'center'
        }
    },
    mobileButton: {
        margin: '5px'
    },
    mobileIcon: {
        '& > img': {
            width: '19px'
        },
        width: '19px',
        marginLeft: '5px'
    },
    subheading: {
        margin: '20px 0 10px'
    },
    paragraph: {
        fontSize: '16px',
        marginBottom: '10px',
        fontWeight: '100'
    },
    link: globalStyles.link,
    emailInput: {
        maxWidth: 162
    },
    sendButton: {
        height: '56px',
        margin: '0 0 0 10px',
        padding: '0.4em'
    },
    form: {
        marginTop: 30
    },
    error: {
        margin: '20px 20px 10px',
        color: red[500],
        minHeight: '20px'
    },
    asterisk: {
        display: 'none'
    },
    phoneParent: {
        display: 'flex',
        justifyContent: 'center'
    },
    phone: {
        maxWidth: 195,
        margin: '0 0 0px',
        display: 'inline-block',
        '& .MobileInputWrapper': {
            marginLeft: '0 !important',
            marginRight: '0 !important'
        }
    }
});

class SendTicket extends Component {
    constructor(props) {
        super(props);

        this.state = {
            email: '',
            phoneNumber: '',
            loading: false,
            error: '',
            smsError: '',
            sent: false,
            smsSent: false,
            countryCode: process.env.REACT_APP_DEFAULT_PHONE_COUNTRY || 'GB'
        };
    }

    handleEmailChange = event => {
        this.setState({
            email: event.target.value.trim()
        });
    };

    handlePhoneChange = number => {
        this.setState({
            phoneNumber: number
        });
    };

    handleCountryCodeChange = code => {
        this.setState({
            countryCode: code
        });
    };

    handleEmailSubmit = async event => {
        event.preventDefault();
        this.setState({
            loading: true,
            error: ''
        });

        try {
            const currentSession = await Auth.currentSession();

            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            const { customerTicketId } = this.props;
            const { email } = this.state;

            await axios.post(
                `${
                    process.env.REACT_APP_PAYMENT_API_ENDPOINT
                }/customer-ticket/send/${customerTicketId}`,
                {
                    email
                },
                {
                    headers
                }
            );

            this.setState({
                loading: false,
                sent: true
            });
        } catch (e) {
            console.error(e);
            this.setState({
                loading: false,
                error: 'Ticket could not be sent. Please try again later.'
            });
        }
    };

    handleSmsSubmit = async event => {
        event.preventDefault();
        this.setState({
            loading: true,
            smsError: ''
        });

        const { phoneNumber, countryCode } = this.state;

        if (!isValidPhoneNumber(phoneNumber, countryCode)) {
            this.setState({
                loading: false,
                smsError: getInvalidPhoneNumberMessage(phoneNumber, countryCode)
            });
            return;
        }

        try {
            const currentSession = await Auth.currentSession();

            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            const { customerTicketId } = this.props;

            await axios.post(
                `${
                    process.env.REACT_APP_PAYMENT_API_ENDPOINT
                }/customer-ticket/send-sms/${customerTicketId}`,
                {
                    phone_number: phoneNumber
                },
                {
                    headers
                }
            );

            this.setState({
                loading: false,
                smsSent: true
            });
        } catch (e) {
            console.error(e);
            this.setState({
                loading: false,
                smsError: 'Ticket could not be sent. Please try again later.'
            });
        }
    };

    render() {
        const { classes, customerTicketId, history } = this.props;
        const {
            loading,
            error,
            smsError,
            email,
            phoneNumber,
            sent,
            smsSent
        } = this.state;

        if (smsSent) {
            history.push(`/ticket/${customerTicketId}/sms-sent`);
        }

        if (sent) {
            history.push(`/ticket/${customerTicketId}/sent`);
        }

        return (
            <>
                <form
                    className={classes.form}
                    onSubmit={this.handleEmailSubmit}
                >
                    <div className={classes.name}>
                        <TextField
                            autoComplete="off"
                            className={`${classes.emailInput}`}
                            id="email"
                            type="email"
                            label="Passenger email"
                            value={email}
                            onChange={this.handleEmailChange}
                            variant="outlined"
                            disabled={loading}
                            required
                            InputLabelProps={{
                                classes: {
                                    asterisk: classes.asterisk
                                }
                            }}
                        />
                        <Button
                            variant="outlined"
                            color="primary"
                            className={`${classes.button} ${
                                classes.sendButton
                            }`}
                            disabled={loading}
                            type="submit"
                        >
                            Send Email
                        </Button>
                    </div>
                    {error !== '' && (
                        <Typography className={classes.error} variant="body2">
                            {error}
                        </Typography>
                    )}
                </form>
                <Typography
                    style={{ marginTop: 30 }}
                    className={classes.paragraph}
                    variant="body2"
                >
                    - OR -
                </Typography>
                <form className={classes.form} onSubmit={this.handleSmsSubmit}>
                    <div className={classes.name}>
                        <div className={classes.phoneParent}>
                            <div className={classes.phone}>
                                <PhoneNumberInput
                                    number={phoneNumber}
                                    onChange={this.handlePhoneChange}
                                    onCountryChange={
                                        this.handleCountryCodeChange
                                    }
                                    errorMessage={null}
                                    placeholder="Phone"
                                    label="Mobile number"
                                />
                            </div>
                            <Button
                                variant="outlined"
                                color="primary"
                                className={`${classes.button} ${
                                    classes.sendButton
                                }`}
                                disabled={loading}
                                type="submit"
                            >
                                Send SMS
                            </Button>
                        </div>
                    </div>
                    {smsError !== '' && (
                        <Typography className={classes.error} variant="body2">
                            {smsError}
                        </Typography>
                    )}
                </form>
            </>
        );
    }
}

SendTicket.propTypes = {
    classes: PropTypes.shape().isRequired,
    customerTicketId: PropTypes.string.isRequired
};

export default withStyles(styles)(withRouter(SendTicket));
